import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Clock, Users, AlertCircle, Gift } from 'lucide-react';

const Catering = () => {
  const cateringOptions = [
    {
      size: "50 pieces",
      price: "$75",
      feeds: "Up to 30 people",
      flavors: "Up to 2 flavor options",
      includes: "1 small sweet chili sauce bottle",
      popular: false
    },
    {
      size: "100 pieces", 
      price: "$150",
      feeds: "Up to 50 people",
      flavors: "Up to 4 flavor options",
      includes: "1 small sweet chili sauce bottle",
      popular: true
    },
    {
      size: "200 pieces",
      price: "$300", 
      feeds: "Up to 100 people",
      flavors: "Up to 6 flavor options",
      includes: "2 small sweet chili sauce bottles",
      popular: false
    }
  ];

  const lumpia_flavors = [
    "Doobie I - Pork & Shrimp Shanghai",
    "Doobie II - Pork Shanghai", 
    "The Joint - Chicken Shanghai",
    "The Blunt - Beef & Vegetable",
    "The Spliff - Shrimp Jalapeno Popper",
    "The Fatty - Bacon Jalapeno Popper",
    "The Reefer - Crab Rangoon"
  ];

  return (
    <section id="catering" className="py-20 bg-gradient-to-b from-yellow-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Catering Services
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Perfect for parties, corporate events, and special occasions. Our catering packages 
            bring authentic Filipino flavors to your gathering with convenient serving sizes.
          </p>
        </div>

        {/* Important Notice */}
        <Alert className="mb-12 border-yellow-300 bg-yellow-50">
          <AlertCircle className="h-5 w-5 text-yellow-600" />
          <AlertDescription className="text-yellow-800 font-medium">
            <strong>Important:</strong> 48 hours notice required for all catering orders. 
            A 50% deposit is due upon placing the order to secure your date.
          </AlertDescription>
        </Alert>

        {/* Catering Packages */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {cateringOptions.map((option, index) => (
            <Card key={index} className={`relative hover:shadow-xl transition-all duration-300 ${
              option.popular 
                ? 'border-4 border-yellow-400 bg-gradient-to-br from-yellow-50 to-amber-50' 
                : 'border-2 border-yellow-200 bg-white'
            }`}>
              {option.popular && (
                <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-gray-900 px-6 py-1 font-bold text-sm">
                  Most Popular
                </Badge>
              )}
              
              <CardHeader className="text-center pb-4">
                <CardTitle className="text-3xl font-bold text-gray-900 mb-2">
                  {option.size}
                </CardTitle>
                <div className="text-4xl font-bold text-gray-800 mb-2">
                  {option.price}
                </div>
                <CardDescription className="text-gray-700 font-semibold flex items-center justify-center space-x-1">
                  <Users className="h-4 w-4" />
                  <span>{option.feeds}</span>
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-start space-x-2">
                    <Gift className="h-5 w-5 text-indigo-600 mt-0.5" />
                    <span className="text-gray-800 font-medium">{option.flavors}</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Gift className="h-5 w-5 text-indigo-600 mt-0.5" />
                    <span className="text-gray-800 font-medium">{option.includes}</span>
                  </div>
                </div>
                
                <button className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                  option.popular
                    ? 'bg-yellow-400 text-gray-900 hover:bg-yellow-300'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}>
                  Order Catering
                </button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Available Flavors */}
        <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-indigo-900 text-center mb-4">
              Available Lumpia Flavors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {lumpia_flavors.map((flavor, index) => (
                <div key={index} className="bg-white rounded-lg p-4 shadow-sm border border-indigo-100">
                  <span className="text-indigo-800 font-medium">{flavor}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Additional Info */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <Card className="bg-white border-2 border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-red-900">
                <Clock className="h-5 w-5" />
                <span>Timing</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-red-800 font-medium">Final balance due before pickup or delivery</p>
            </CardContent>
          </Card>

          <Card className="bg-white border-2 border-orange-200">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-orange-900">
                <AlertCircle className="h-5 w-5" />
                <span>Cancellation</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-orange-800 font-medium">Cancellations made within 24 hours will forfeit the 50% deposit</p>
            </CardContent>
          </Card>

          <Card className="bg-white border-2 border-emerald-200">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-emerald-900">
                <Gift className="h-5 w-5" />
                <span>Custom Orders</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-emerald-800 font-medium">Special requests or custom modifications may incur a $20 up-charge</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Catering;